import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    PdfLoader, PdfHighlighter, Highlight, AreaHighlight, Popup, Tip,
} from "react-pdf-highlighter";
import "react-pdf-highlighter/dist/style.css";
import { ArrowLeft, Trash2, Save, Loader2, Mail, Download, Type, Square } from "lucide-react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Chip } from "@/components/ui-bits";

// Same-origin PDF.js worker — Safari (esp. under strict CSP) refuses the inline
// blob-URL worker that pdf.js falls back to when no workerSrc is set. Serving the
// worker from /public keeps everything on-origin and avoids "blob://null" errors.
const PDF_WORKER_SRC = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

// Palette-aligned highlight colors (yellow, green, rust)
const COLORS = [
    { key: "#FFEB3B", label: "yellow" },
    { key: "#A6D48D", label: "green" },
    { key: "#E6B5A6", label: "rust" },
];

/**
 * Full-page PDF/Word annotator.
 * Route: /projects/:projectId/versions/:versionId/annotate
 */
export default function Annotator() {
    const { projectId, versionId } = useParams();
    const nav = useNavigate();

    const [project, setProject] = useState(null);
    const [version, setVersion] = useState(null);
    const [annotations, setAnnotations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfLoadError, setPdfLoadError] = useState(null);
    const [color, setColor] = useState(COLORS[0].key);
    const [mode, setMode] = useState("text"); // "text" = highlight text, "area" = draw a box over graphics/anywhere
    const [emailOpen, setEmailOpen] = useState(false);
    const scrollViewerToRef = useRef(() => {});

    const [pdfBlobUrl, setPdfBlobUrl] = useState(null);

    // Fetch the PDF ourselves (rather than letting PdfLoader do it) so we can:
    //   (a) show a clearer progress message during Word→PDF conversion,
    //   (b) surface HTTP errors instead of hanging on "Loading PDF…", and
    //   (c) hand PdfLoader a stable blob URL.
    //
    // IMPORTANT: React StrictMode remounts effects. If we naïvely put the blob
    // URL in state and revoke it in a cleanup, the FIRST mount's cleanup
    // revokes the URL while PdfLoader is still trying to consume it → the
    // browser reports ERR_FILE_NOT_FOUND / UnexpectedResponseException even
    // though the /pdf response was 200.
    //
    // Solution: single-effect with an AbortController + a stable local `url`
    // variable. We revoke exactly ONCE, at unmount, and only if the fetch
    // completed. Stale fetches are cancelled cleanly.
    // Track the blob URL currently handed to PdfLoader in a ref, separate
    // from the effect's local scope. This way React StrictMode's double-mount
    // (which invokes the cleanup between mounts) doesn't yank the URL out from
    // under PdfLoader mid-load. We only revoke a URL when we're SURE a newer
    // one has taken its place — or on final page unload.
    const activeBlobUrlRef = useRef(null);

    const [retryTick, setRetryTick] = useState(0);
    const retry = useCallback(() => {
        setPdfLoadError(null);
        setPdfBlobUrl(null);
        setRetryTick((n) => n + 1);
    }, []);
    useEffect(() => {
        if (!versionId) return undefined;
        let cancelled = false;
        const ctrl = new AbortController();

        (async () => {
            setPdfLoadError(null);
            setPdfBlobUrl(null);
            try {
                const res = await fetch(`${API}/versions/${versionId}/pdf`, {
                    credentials: "same-origin",
                    signal: ctrl.signal,
                });
                if (cancelled) return;
                if (!res.ok) {
                    let detail = `HTTP ${res.status}`;
                    try {
                        const j = await res.json();
                        detail = j?.detail || (j?.detail && j.detail.detail) || detail;
                    } catch (parseErr) {
                        // Response wasn't JSON (usually a plain-text error from the
                        // proxy) — fall back to the HTTP status line already in `detail`.
                        console.debug("PDF error response was not JSON:", parseErr);
                    }
                    if (!cancelled) setPdfLoadError(detail);
                    return;
                }
                const blob = await res.blob();
                if (cancelled) return;
                if (!blob || blob.size === 0) {
                    setPdfLoadError("Empty response from the server");
                    return;
                }
                const newUrl = URL.createObjectURL(blob);
                // Revoke the PREVIOUS URL only now that we have a working new one.
                const prev = activeBlobUrlRef.current;
                activeBlobUrlRef.current = newUrl;
                setPdfBlobUrl(newUrl);
                if (prev && prev !== newUrl) {
                    // Give PdfLoader one paint tick to swap sources before revoking.
                    setTimeout(() => URL.revokeObjectURL(prev), 0);
                }
            } catch (e) {
                if (e?.name === "AbortError" || cancelled) return;
                setPdfLoadError(e?.message || "Network error");
            }
        })();

        // Cancel the in-flight fetch on unmount/re-run — but DO NOT revoke the
        // active blob URL here. PdfLoader may still be consuming it, especially
        // during React StrictMode's synthetic double-mount. The active URL is
        // revoked when its successor arrives (see above) or on full page unload.
        return () => {
            cancelled = true;
            ctrl.abort();
        };
    }, [versionId, retryTick]);

    // Belt-and-suspenders: release the final blob URL when the browser tears
    // down the page (or the user navigates away entirely).
    useEffect(() => {
        const unload = () => {
            if (activeBlobUrlRef.current) URL.revokeObjectURL(activeBlobUrlRef.current);
        };
        window.addEventListener("pagehide", unload);
        return () => {
            window.removeEventListener("pagehide", unload);
            // Component-unmount revoke: safe because PdfLoader is being torn down too.
            if (activeBlobUrlRef.current) {
                URL.revokeObjectURL(activeBlobUrlRef.current);
                activeBlobUrlRef.current = null;
            }
        };
    }, []);

    const pdfUrl = pdfBlobUrl || `${API}/versions/${versionId}/pdf`;

    const fetchAll = useCallback(async () => {
        try {
            // We deliberately DON'T call GET /versions/{id} directly — that route
            // doesn't exist and returns 405, cluttering the console. Instead, load
            // the parent project's versions list and pick this one out.
            const [anns, list, p] = await Promise.all([
                api.get(`/versions/${versionId}/annotations`).then((r) => r.data),
                projectId
                    ? api.get(`/projects/${projectId}/versions`).then((r) => r.data)
                    : Promise.resolve([]),
                projectId
                    ? api.get(`/projects/${projectId}`).then((r) => r.data).catch(() => null)
                    : Promise.resolve(null),
            ]);
            const vv = list.find((x) => x.id === versionId) || null;
            setVersion(vv);
            setAnnotations(anns);
            setProject(p);
        } catch (e) {
            toast.error("Failed to load");
        } finally {
            setLoading(false);
        }
    }, [projectId, versionId]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    // Convert our stored annotations into react-pdf-highlighter's format.
    const highlights = useMemo(
        () => annotations.map((a) => ({
            id: a.id,
            position: {
                boundingRect: rectToRPH(a.bounding_rect, a.viewport),
                rects: (a.rects || []).map((r) => rectToRPH(r, a.viewport)),
                pageNumber: a.page_number || 1,
            },
            content: a.type === "note"
                ? {} // area/blank content for notes
                : { text: a.text || "" },
            comment: { text: a.comment || "", emoji: "" },
            color: a.color,
            type: a.type,
        })),
        [annotations]
    );

    const createAnnotation = async (payload) => {
        try {
            const res = await api.post(`/versions/${versionId}/annotations`, payload);
            setAnnotations((prev) => [...prev, res.data]);
            toast.success("Annotation added");
        } catch (e) {
            toast.error("Failed to add annotation");
        }
    };

    const updateComment = async (id, comment) => {
        try {
            const res = await api.patch(`/annotations/${id}`, { comment });
            setAnnotations((prev) => prev.map((a) => (a.id === id ? res.data : a)));
        } catch (e) {
            toast.error("Failed to update");
        }
    };

    const deleteAnnotation = async (id) => {
        try {
            await api.delete(`/annotations/${id}`);
            setAnnotations((prev) => prev.filter((a) => a.id !== id));
            toast.success("Annotation removed");
        } catch (e) {
            toast.error("Failed to remove");
        }
    };

    const downloadAnnotated = async () => {
        try {
            const res = await fetch(`${API}/versions/${versionId}/annotated.pdf`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const base = (version?.filename || "draft").replace(/\.[^.]+$/, "");
            a.download = `${base}_annotated.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Downloaded annotated PDF");
        } catch (e) {
            toast.error(e.message || "Download failed");
        }
    };

    if (loading) {
        return <div className="p-10 pulse-mono font-mono text-sm text-muted-foreground">Loading annotator…</div>;
    }
    if (!version) {
        return (
            <div className="p-10">
                <button className="btn-ghost" onClick={() => nav(`/projects/${projectId}`)}>
                    <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <div className="mt-6">Version not found.</div>
            </div>
        );
    }
    if (version.file_missing) {
        return (
            <div className="p-10 max-w-2xl" data-testid="annotator-file-lost">
                <button className="btn-ghost" onClick={() => nav(`/projects/${projectId}`)} data-testid="annotator-back">
                    <ArrowLeft className="w-4 h-4" /> Back to project
                </button>
                <div className="mt-8 paper border border-[#8C2D19]/40 bg-[#8C2D19]/[0.04] rounded-sm p-8">
                    <div className="label-mono text-[#8C2D19] mb-2">File lost</div>
                    <h1 className="font-serif text-4xl leading-tight mb-3">
                        The file for {version.label || `v${version.version_number}`} isn&apos;t on the server anymore.
                    </h1>
                    <p className="text-foreground/80 leading-relaxed">
                        This can happen on older records after a redeploy. New uploads are stored in
                        persistent object storage and won&apos;t vanish again. Head back to the Development tab
                        and use <span className="font-semibold">Re-upload</span> on this version&apos;s card to restore it.
                    </p>
                    <div className="mt-6">
                        <button
                            className="btn-primary"
                            onClick={() => nav(`/projects/${projectId}`)}
                            data-testid="annotator-goto-development"
                        >
                            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Go to Development
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-40 flex flex-col bg-background" data-testid="annotator">
            {/* Top bar */}
            <div className="border-b border-border p-4 flex items-center gap-4 flex-wrap">
                <button
                    className="btn-ghost"
                    onClick={() => nav(`/projects/${projectId}`)}
                    data-testid="annotator-back"
                >
                    <ArrowLeft className="w-4 h-4" /> Back to project
                </button>
                <div className="min-w-0">
                    <div className="label-mono">Annotating</div>
                    <div className="font-serif text-xl leading-tight truncate">
                        {project?.title || "…"}
                        <span className="text-muted-foreground text-base"> · {version.label || `v${version.version_number}`}</span>
                    </div>
                </div>
                <div className="grow" />
                <div className="flex items-center gap-1 border border-border rounded-sm p-0.5 bg-secondary/40" data-testid="annotation-mode-toggle">
                    <button
                        onClick={() => setMode("text")}
                        data-testid="mode-text"
                        title="Drag over text to highlight it"
                        className={
                            "flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono rounded-sm transition-colors " +
                            (mode === "text"
                                ? "bg-background text-foreground shadow-sm"
                                : "text-muted-foreground hover:text-foreground")
                        }
                    >
                        <Type className="w-3.5 h-3.5" strokeWidth={1.5} /> Highlight text
                    </button>
                    <button
                        onClick={() => setMode("area")}
                        data-testid="mode-area"
                        title="Drag anywhere (including over illustrations) to draw a note area"
                        className={
                            "flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono rounded-sm transition-colors " +
                            (mode === "area"
                                ? "bg-background text-foreground shadow-sm"
                                : "text-muted-foreground hover:text-foreground")
                        }
                    >
                        <Square className="w-3.5 h-3.5" strokeWidth={1.5} /> Note on area
                    </button>
                </div>
                <div className="flex items-center gap-2" data-testid="color-picker">
                    <div className="label-mono">Color</div>
                    {COLORS.map((c) => (
                        <button
                            key={c.key}
                            onClick={() => setColor(c.key)}
                            data-testid={`color-${c.label}`}
                            className={
                                "w-6 h-6 rounded-full border-2 transition-transform " +
                                (color === c.key ? "border-foreground scale-110" : "border-border")
                            }
                            style={{ background: c.key }}
                            title={c.label}
                        />
                    ))}
                </div>
                <button className="btn-ghost" onClick={downloadAnnotated} data-testid="download-annotated">
                    <Download className="w-4 h-4" strokeWidth={1.5} /> Download annotated
                </button>
                <button className="btn-primary" onClick={() => setEmailOpen(true)} data-testid="email-annotated-btn">
                    <Mail className="w-4 h-4" strokeWidth={1.5} /> Email to author
                </button>
            </div>

            {/* Main body: PDF + sidebar */}
            <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[1fr_360px]">
                <div className="relative bg-[#E1DBCF] overflow-hidden" data-testid="pdf-viewport">
                    {pdfLoadError ? (
                        <PdfError detail={pdfLoadError} onRetry={retry} />
                    ) : !pdfBlobUrl ? (
                        <PdfLoading isWord={version && !/\.pdf$/i.test(version.filename || "")} />
                    ) : (
                    <PdfLoader
                        url={pdfUrl}
                        workerSrc={PDF_WORKER_SRC}
                        beforeLoad={<PdfLoading />}
                        errorMessage={<PdfError detail={pdfLoadError} onRetry={retry} />}
                        onError={(err) => {
                            const msg = err?.message || String(err);
                            console.error("PdfLoader failed:", err);
                            setPdfLoadError(msg);
                        }}
                    >
                        {(pdfDocument) => (
                            <PdfHighlighter
                                pdfDocument={pdfDocument}
                                enableAreaSelection={(event) => mode === "area" || event.altKey}
                                onScrollChange={() => {}}
                                scrollRef={(scrollTo) => { scrollViewerToRef.current = scrollTo; }}
                                highlights={highlights}
                                pdfScaleValue="page-width"
                                // eslint-disable-next-line react/no-unstable-nested-components
                                onSelectionFinished={(position, content, hideTipAndSelection) => (
                                    <SelectionTip
                                            onConfirm={async (kind, comment) => {
                                                const viewport = position.boundingRect;
                                                const vp = { width: viewport.width, height: viewport.height };
                                                const bounding_rect = rectToArr(position.boundingRect);
                                                const rects = (position.rects || []).map(rectToArr);
                                                await createAnnotation({
                                                    type: kind === "note" ? "note" : "highlight",
                                                    page_number: position.pageNumber,
                                                    rects: rects.length ? rects : [bounding_rect],
                                                    bounding_rect,
                                                    viewport: vp,
                                                    text: content?.text || "",
                                                    comment: comment || "",
                                                    color,
                                                });
                                                hideTipAndSelection();
                                            }}
                                        />
                                )}
                                // eslint-disable-next-line react/no-unstable-nested-components
                                highlightTransform={(highlight, index, setTip, hideTip, viewportToScaled, screenshot, isScrolledTo) => {
                                    const isText = !!highlight.content?.text;
                                    const HL = isText ? Highlight : AreaHighlight;
                                    const component = (
                                        <HL
                                            isScrolledTo={isScrolledTo}
                                            position={highlight.position}
                                            comment={highlight.comment}
                                            style={{ background: highlight.color, opacity: 0.5 }}
                                        />
                                    );
                                    return (
                                        <Popup
                                            popupContent={
                                                <SidePopup
                                                    highlight={highlight}
                                                    onEdit={(c) => updateComment(highlight.id, c)}
                                                    onDelete={() => deleteAnnotation(highlight.id)}
                                                />
                                            }
                                            onMouseOver={(popupContent) => setTip(highlight, () => popupContent)}
                                            onMouseOut={hideTip}
                                            key={highlight.id}
                                        >
                                            {component}
                                        </Popup>
                                    );
                                }}
                            />
                        )}
                    </PdfLoader>
                    )}
                </div>

                {/* Sidebar list */}
                <aside className="border-l border-border overflow-auto p-4" data-testid="annotation-sidebar">
                    <div className="label-mono mb-2">Annotations · {annotations.length}</div>
                    <p className="text-xs text-muted-foreground mb-4" data-testid="annotation-hint">
                        {mode === "text" ? (
                            <>
                                Drag over text to highlight it. Switch to{" "}
                                <span className="font-mono">Note on area</span> above (or hold{" "}
                                <kbd className="px-1 py-0.5 border rounded text-[10px]">Alt</kbd>/
                                <kbd className="px-1 py-0.5 border rounded text-[10px]">⌥</kbd> + drag) to mark up illustrations or margins.
                            </>
                        ) : (
                            <>
                                <span className="font-mono">Note on area</span> is on — drag anywhere on the page (including over graphics) to draw a note. Switch back to <span className="font-mono">Highlight text</span> to select text.
                            </>
                        )}
                    </p>
                    {annotations.length === 0 && (
                        <div className="text-sm text-muted-foreground">
                            No annotations yet. Try highlighting a line of text.
                        </div>
                    )}
                    <ul className="space-y-3">
                        {annotations.map((a) => (
                            <li key={a.id} className="paper border border-border rounded-sm p-3 text-sm" data-testid={`ann-item-${a.id}`}>
                                <div className="flex items-center gap-2 mb-1">
                                    <span
                                        className="w-3 h-3 rounded-sm border border-border"
                                        style={{ background: a.color }}
                                    />
                                    <Chip>{a.type === "note" ? "note" : "highlight"}</Chip>
                                    <span className="label-mono">p. {a.page_number}</span>
                                    <div className="grow" />
                                    <button
                                        onClick={() => deleteAnnotation(a.id)}
                                        className="text-muted-foreground hover:text-[#8C2D19]"
                                        data-testid={`ann-delete-${a.id}`}
                                    >
                                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                                    </button>
                                </div>
                                {a.text && (
                                    <blockquote className="text-xs italic text-muted-foreground border-l-2 border-border pl-2 mb-2">
                                        &ldquo;{a.text.slice(0, 200)}&rdquo;
                                    </blockquote>
                                )}
                                <InlineEditableComment
                                    initial={a.comment}
                                    onSave={(c) => updateComment(a.id, c)}
                                    testId={`ann-comment-${a.id}`}
                                />
                            </li>
                        ))}
                    </ul>
                </aside>
            </div>

            {emailOpen && (
                <EmailAnnotatedDialog
                    projectId={projectId}
                    versionId={versionId}
                    project={project}
                    version={version}
                    onClose={() => setEmailOpen(false)}
                />
            )}
        </div>
    );
}

function PdfLoading({ isWord = false }) {
    return (
        <div className="w-full h-full flex items-center justify-center">
            <div className="text-center max-w-md">
                <div className="pulse-mono font-mono text-sm text-muted-foreground mb-2">
                    {isWord ? "Converting Word document to PDF…" : "Loading PDF…"}
                </div>
                {isWord ? (
                    <div className="text-xs text-muted-foreground font-mono">
                        This can take 30-60 seconds the first time.
                    </div>
                ) : null}
            </div>
        </div>
    );
}
function PdfError({ detail, onRetry }) {
    return (
        <div className="p-8" data-testid="pdf-error">
            <div className="paper border border-border rounded-sm p-6 max-w-md mx-auto">
                <h3 className="font-serif text-2xl mb-2">Couldn&apos;t open this file.</h3>
                <p className="text-sm text-muted-foreground">
                    If this is a Word file, the server converts it to PDF the first time — that can take a few seconds.
                    Try again below. If it still fails, the source file may be corrupted or the conversion tool may be unavailable.
                </p>
                {onRetry ? (
                    <button
                        className="btn-primary mt-4"
                        onClick={onRetry}
                        data-testid="pdf-error-retry"
                    >
                        Try again
                    </button>
                ) : null}
                {detail ? (
                    <details className="mt-4 text-xs text-muted-foreground" data-testid="pdf-error-detail">
                        <summary className="cursor-pointer select-none">Technical details</summary>
                        <pre className="mt-2 whitespace-pre-wrap font-mono text-[11px] p-2 bg-secondary/40 border border-border rounded-sm">{detail}</pre>
                    </details>
                ) : null}
            </div>
        </div>
    );
}

function SelectionTip({ onConfirm }) {
    const [comment, setComment] = useState("");
    return (
        <div className="paper border border-border rounded-sm p-3 bg-background shadow-lg" style={{ minWidth: 260 }}>
            <div className="label-mono mb-2">Add note</div>
            <textarea
                autoFocus
                className="input-flat min-h-[64px]"
                placeholder="Your comment (optional)…"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                data-testid="selection-tip-comment"
            />
            <div className="flex gap-2 mt-2 justify-end">
                <button
                    className="btn-primary text-xs"
                    onClick={() => onConfirm("highlight", comment)}
                    data-testid="selection-tip-save"
                >
                    <Save className="w-3.5 h-3.5" strokeWidth={1.5} /> Save
                </button>
            </div>
        </div>
    );
}

function SidePopup({ highlight }) {
    return (
        <div className="paper border border-border rounded-sm p-2 text-xs max-w-xs shadow-lg">
            {highlight.comment?.text || <span className="text-muted-foreground">No comment</span>}
        </div>
    );
}

function InlineEditableComment({ initial, onSave, testId }) {
    const [editing, setEditing] = useState(false);
    const [val, setVal] = useState(initial || "");
    useEffect(() => { setVal(initial || ""); }, [initial]);

    if (!editing) {
        return (
            <div
                className="text-xs whitespace-pre-wrap cursor-text"
                onClick={() => setEditing(true)}
                data-testid={testId}
            >
                {initial || <span className="text-muted-foreground italic">Click to add a comment…</span>}
            </div>
        );
    }
    return (
        <div>
            <textarea
                autoFocus
                className="input-flat text-xs min-h-[70px]"
                value={val}
                onChange={(e) => setVal(e.target.value)}
                data-testid={`${testId}-input`}
            />
            <div className="flex gap-1 justify-end mt-1">
                <button className="btn-ghost text-xs" onClick={() => { setEditing(false); setVal(initial || ""); }}>
                    Cancel
                </button>
                <button
                    className="btn-primary text-xs"
                    onClick={() => { onSave(val); setEditing(false); }}
                    data-testid={`${testId}-save`}
                >
                    Save
                </button>
            </div>
        </div>
    );
}

function EmailAnnotatedDialog({ versionId, project, version, onClose }) {
    const [toName, setToName] = useState(project?.author || "");
    const [toEmail, setToEmail] = useState(project?.author_email || "");
    const [subject, setSubject] = useState(
        `${project?.title || ""} — ${version.label || `Draft v${version.version_number}`} (annotated)`
    );
    const [message, setMessage] = useState("");
    const [busy, setBusy] = useState(false);

    const send = async () => {
        if (!toEmail.trim() || !toEmail.includes("@")) return toast.error("Enter a valid author email");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("to_email", toEmail.trim());
            fd.append("to_name", toName);
            fd.append("subject", subject);
            fd.append("message", message);
            const res = await api.post(`/versions/${versionId}/email-annotated`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Sent to ${res.data.to} with ${res.data.annotations} annotation${res.data.annotations === 1 ? "" : "s"}`);
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="email-annotated-dialog">
            <div className="paper border border-border rounded-sm w-full max-w-lg bg-background p-8">
                <div className="label-mono mb-2">Send annotated PDF</div>
                <h3 className="font-serif text-3xl mb-6">
                    Email marked-up {version.label || `v${version.version_number}`} to author
                </h3>
                <div className="space-y-4">
                    <label className="block">
                        <span className="label-mono">Author name</span>
                        <input className="input-flat mt-1" value={toName} onChange={(e) => setToName(e.target.value)} data-testid="ea-name" />
                    </label>
                    <label className="block">
                        <span className="label-mono">Author email *</span>
                        <input className="input-flat mt-1 font-mono" value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="author@example.com" data-testid="ea-email" />
                    </label>
                    <label className="block">
                        <span className="label-mono">Subject</span>
                        <input className="input-flat mt-1" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="ea-subject" />
                    </label>
                    <label className="block">
                        <span className="label-mono">Note (optional)</span>
                        <textarea className="input-flat mt-1 min-h-[100px]" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Leave blank for a default note." data-testid="ea-message" />
                    </label>
                </div>
                <div className="flex gap-2 justify-end mt-6">
                    <button className="btn-ghost" onClick={onClose} data-testid="ea-cancel">Cancel</button>
                    <button className="btn-primary disabled:opacity-60" onClick={send} disabled={busy} data-testid="ea-submit">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" strokeWidth={1.5} />}
                        {busy ? "Sending…" : "Send annotated PDF"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------------
// Coordinate helpers.
// react-pdf-highlighter uses `boundingRect` as {x1,y1,x2,y2, width, height}.
// Our backend stores [x1,y1,x2,y2] + a separate viewport {width,height}.
// -----------------------------------------------------------------------------

function rectToArr(r) {
    return [r.x1, r.y1, r.x2, r.y2];
}
function rectToRPH(arr, viewport) {
    const [x1, y1, x2, y2] = arr || [0, 0, 0, 0];
    return {
        x1, y1, x2, y2,
        width: (viewport && viewport.width) || 0,
        height: (viewport && viewport.height) || 0,
    };
}
